import { useEffect, useState } from "react";
import { X, Upload, Gift } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const AREAS = ["Whitefield","Sarjapur Road","Electronic City","HSR Layout","Koramangala","Indiranagar","Hebbal","Yelahanka","Jayanagar","JP Nagar","Bannerghatta Road","KR Puram","Marathahalli","Bellandur","Other"];
const BUDGETS = ["Under ₹10L","₹10L - ₹15L","₹15L - ₹25L","₹25L - ₹40L","₹40L - ₹50L+"];

export default function ConsultPopup() {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ name:"", phone:"", email:"", location:"", budget:"₹15L - ₹25L" });

  useEffect(() => {
    if (sessionStorage.getItem("di_popup_shown")) return;
    const show = () => {
      if (sessionStorage.getItem("di_popup_shown")) return;
      sessionStorage.setItem("di_popup_shown", "1");
      setOpen(true);
    };
    const t = setTimeout(show, 20000);
    const onLeave = (e) => { if (e.clientY <= 0) show(); };
    document.addEventListener("mouseleave", onLeave);
    return () => { clearTimeout(t); document.removeEventListener("mouseleave", onLeave); };
  }, []);

  // Lock background page scroll while modal is open; restore on close/unmount.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const closePopup = () => setOpen(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    let floor_plan_path = null;
    try {
      if (file) {
        const fd = new FormData(); fd.append("file", file); fd.append("folder", "floorplans");
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        floor_plan_path = r.data.path;
      }
      await api.post("/leads", {
        name: form.name, email: form.email, phone: form.phone,
        budget: form.budget, message: `[Exit-intent popup] Locality: ${form.location}`,
        floor_plan_path,
        source: "Website Consultation Popup",
      });
      setDone(true);
      toast.success("Booked! We'll call within 24 hours.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit");
    } finally { setSubmitting(false); }
  };

  if (!open) return null;

  return (
    <div
      data-testid="consult-popup"
      className="fixed inset-0 z-[60] overflow-y-auto overscroll-contain bg-black/60 backdrop-blur-sm animate-fadeIn"
      role="dialog"
      aria-modal="true"
      aria-labelledby="consult-popup-title"
      onClick={closePopup}
    >
      {/* Close button — pinned to viewport, always tappable, never scrolls off-screen */}
      <button
        type="button"
        data-testid="popup-close"
        aria-label="Close consultation popup"
        onClick={(e) => { e.stopPropagation(); closePopup(); }}
        className="fixed z-[70] h-10 w-10 rounded-full bg-white/95 hover:bg-white grid place-items-center shadow-lg"
        style={{
          top: "max(0.75rem, env(safe-area-inset-top, 0px))",
          right: "max(0.75rem, env(safe-area-inset-right, 0px))",
        }}
      >
        <X size={18} />
      </button>

      <div
        className="min-h-full w-full flex items-center justify-center p-3 sm:p-6"
        style={{
          paddingTop: "max(0.75rem, env(safe-area-inset-top, 0px))",
          paddingBottom: "max(0.75rem, env(safe-area-inset-bottom, 0px))",
        }}
      >
        <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-3xl bg-[#FBF8F2] rounded-2xl overflow-hidden shadow-2xl grid grid-cols-1 md:grid-cols-5 animate-fadeUp">

        <div className="md:col-span-2 relative bg-[#1C1A17] p-8 md:p-10 text-white">
          <Gift className="text-[#C9A24A]" size={28} strokeWidth={1.4}/>
          <p className="overline text-[#E8D9B3] mt-6">Limited Offer</p>
          <h2 id="consult-popup-title" className="font-heading text-3xl md:text-4xl font-light mt-3 leading-[1.1]">
            A free <em className="italic text-[#E8D9B3]">30-minute</em> interior consultation.
          </h2>
          <p className="mt-5 text-sm text-white/70 leading-relaxed">
            Before you go — our senior designer will walk through your floor plan, materials, and a transparent budget. No obligations.
          </p>
          <ul className="mt-6 space-y-2 text-xs text-white/80">
            <li>✦ Worth ₹3,500 — free this week</li>
            <li>✦ In-office or video call</li>
            <li>✦ Custom material recommendations</li>
          </ul>
        </div>

        {done ? (
          <div data-testid="popup-success" className="md:col-span-3 p-8 md:p-10 flex flex-col justify-center">
            <p className="overline">Booked</p>
            <h3 className="font-heading text-3xl mt-3">Thank you, {form.name.split(" ")[0]}.</h3>
            <p className="text-[#5C5752] mt-4 text-sm leading-relaxed">Our senior designer will call <strong>{form.phone}</strong> within 24 hours to confirm a slot.</p>
            <button type="button" onClick={closePopup} className="btn-primary mt-8 self-start">Continue browsing</button>
          </div>
        ) : (
          <form onSubmit={submit} className="md:col-span-3 p-8 md:p-10 space-y-4">
            <p className="overline">Claim your slot</p>
            <h3 className="font-heading text-2xl">Tell us about your home.</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <input required data-testid="popup-name" placeholder="Full name *" className="input-line" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})}/>
              <input required data-testid="popup-phone" placeholder="Phone *" className="input-line" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})}/>
            </div>
            <input required type="email" data-testid="popup-email" placeholder="Email *" className="input-line" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})}/>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <select required data-testid="popup-location" className="input-line" value={form.location} onChange={(e)=>setForm({...form,location:e.target.value})}>
                <option value="">Project Location *</option>
                {AREAS.map(a => <option key={a}>{a}</option>)}
              </select>
              <select data-testid="popup-budget" className="input-line" value={form.budget} onChange={(e)=>setForm({...form,budget:e.target.value})}>
                {BUDGETS.map(b => <option key={b}>{b}</option>)}
              </select>
            </div>
            <label className="block">
              <span className="text-xs uppercase tracking-[0.25em] text-[#5C5752]">Floor Plan (optional)</span>
              <div className="mt-2 border border-dashed border-[#1C1A17]/30 hover:border-[#1C1A17] cursor-pointer flex items-center gap-3 p-3 rounded-xl transition-colors">
                <Upload size={16} className="text-[#A07F2D]"/>
                <span className="text-sm truncate">{file ? file.name : "PDF, JPG or PNG · max 10MB"}</span>
                <input data-testid="popup-file" type="file" accept=".pdf,.jpg,.jpeg,.png" hidden onChange={(e)=>setFile(e.target.files?.[0]||null)}/>
              </div>
            </label>
            <button type="submit" data-testid="popup-submit" disabled={submitting} className="btn-gold w-full justify-center mt-2">
              {submitting ? "Booking…" : "Claim Free Consultation"}
            </button>
            <p className="text-[10px] text-[#8A847D] text-center">By submitting, you agree to be contacted by Dreamy Interiors.</p>
          </form>
        )}
        </div>
      </div>
    </div>
  );
}
